import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
});

/**
 * Genel amaçlı Edge Function çağrısı (GET / POST).
 * supabase.functions.invoke bazen "Failed to send a request" verebilir; bu düz fetch ile gider.
 */
export async function callEdgeFunction(functionPath, { method = 'GET', body = null } = {}) {
  const base = (supabaseUrl || '').trim().replace(/\/+$/, '');
  const key = (supabaseAnonKey || '').trim();
  if (!base || !key) {
    throw new Error('REACT_APP_SUPABASE_URL veya REACT_APP_SUPABASE_ANON_KEY tanımsız.');
  }

  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token ?? key;
  const url = `${base}/functions/v1/${functionPath}`;

  const opts = {
    method,
    headers: {
      'Content-Type': 'application/json',
      apikey: key,
      Authorization: `Bearer ${token}`,
    },
  };
  if (body) opts.body = JSON.stringify(body);

  const res = await fetch(url, opts);
  const text = await res.text();
  let parsed;
  try { parsed = JSON.parse(text); } catch { parsed = text; }

  if (!res.ok) {
    const msg =
      typeof parsed?.error === 'string'
        ? parsed.error
        : typeof parsed?.message === 'string'
          ? parsed.message
          : typeof parsed?.detail === 'string'
            ? `HTTP ${res.status}: ${parsed.detail}`
            : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return parsed;
}

/**
 * Edge Function çağrısı (düz fetch). Bazı ortamlarda supabase.functions.invoke
 * "Failed to send a request to the Edge Function" verebiliyor; bu yol aynı URL/headers ile gider.
 *
 * @returns {{ userId: string|null, serverError: string|null, networkError: string|null }}
 */
export async function postEdgeFunctionJson(functionName, jsonBody) {
  const base = (supabaseUrl || '').trim().replace(/\/+$/, '');
  const key = (supabaseAnonKey || '').trim();
  if (!base || !key) {
    return {
      userId: null,
      serverError: null,
      networkError:
        'REACT_APP_SUPABASE_URL veya REACT_APP_SUPABASE_ANON_KEY tanımsız. .env kök dizinde olmalı; değişiklikten sonra npm start’ı yeniden başlatın.',
    };
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token ?? key;
  const url = `${base}/functions/v1/${encodeURIComponent(functionName)}`;

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: key,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(jsonBody),
    });
  } catch (e) {
    const msg = e?.message || String(e);
    return {
      userId: null,
      serverError: null,
      networkError: `${msg} — VPN, kurumsal ağ, reklam engelleyici veya güvenlik eklentisi ${url} çağrısını engelliyor olabilir.`,
    };
  }

  let body = null;
  try {
    const text = await res.text();
    if (text) body = JSON.parse(text);
  } catch {
    body = null;
  }

  if (!res.ok) {
    const msg =
      typeof body?.error === 'string'
        ? body.error
        : `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''}`.trim();
    return { userId: null, serverError: msg, networkError: null };
  }

  const userId = body?.user?.id ?? null;
  if (!userId) {
    return {
      userId: null,
      serverError: 'Edge Function 200 döndü ama yanıtta user.id yok.',
      networkError: null,
    };
  }

  return { userId, serverError: null, networkError: null };
}
